// One stuck-media rebuild attempt against the live SIP objects, behind narrow
// interfaces so the ordering that keeps the pool seat can be tested without
// sip.js. use-softphone owns the scheduling, telemetry and backoff around it;
// the decisions come from media-rebuild.ts.
import {
  mediaRebuildSeatGate,
  stuckLineFallback,
  type MediaRebuildSeat,
} from '@/lib/media-rebuild';
import { withTimeout } from '@/lib/softphone-start';

interface StateEmitter {
  addListener: (listener: (data: unknown) => void) => void;
  removeListener: (listener: (data: unknown) => void) => void;
}

interface MediaRebuildRegisterer {
  readonly state: unknown;
  readonly stateChange: StateEmitter;
  unregister: () => Promise<unknown>;
  register: () => Promise<unknown>;
}

interface MediaRebuildSession {
  readonly state: unknown;
  readonly stateChange: StateEmitter;
  bye: () => Promise<unknown>;
}

export type MediaRebuildRunResult =
  | { outcome: 'deferred'; reason: 'phone_not_ready' | 'seat' }
  | { outcome: 'released'; prospectBridged: boolean }
  | { outcome: 'kept'; reason: 'line_not_released' | 'conversation_ended' | 'unregister_unconfirmed' | 'unregister_failed' };

export interface MediaRebuildRunDeps {
  registerer: MediaRebuildRegisterer;
  session: MediaRebuildSession;
  // Transport up, no in-place reconnect or ICE restart running, lease held.
  phoneReady: () => boolean;
  // A fresh membership read; null when it failed, is not seated, or the
  // backend is restarting.
  readSeat: () => Promise<MediaRebuildSeat | null>;
  // Same registerer still installed, runtime not stopped, not a lame duck.
  canReRegister: () => boolean;
  // The re-REGISTER failed after the un-REGISTER completed: nothing else will
  // bring the binding back.
  onReRegisterFailed: () => void;
  // While true the registerer's own recovery and the pool's re-REGISTER
  // nudges stand down.
  setInFlight: (inFlight: boolean) => void;
  timeouts: { signalingMs: number; reapMs: number; bridgedReapMs: number };
}

// Resolves true once `done()` holds (checked now and on every emission),
// false when the timeout lands first.
function waitForEmitter(emitter: StateEmitter, done: () => boolean, timeoutMs: number): Promise<boolean> {
  if (done()) return Promise.resolve(true);
  return new Promise((resolve) => {
    const listener = () => {
      if (!done()) return;
      clearTimeout(timer);
      emitter.removeListener(listener);
      resolve(true);
    };
    const timer = setTimeout(() => {
      emitter.removeListener(listener);
      resolve(false);
    }, timeoutMs);
    emitter.addListener(listener);
  });
}

// After the un-REGISTER landed: wait for the switch to reap the line, or hang
// it up from here when a prospect is still on it.
async function releaseStuckLine(deps: MediaRebuildRunDeps, prospectBridged: boolean): Promise<MediaRebuildRunResult> {
  const { session, timeouts } = deps;
  const reaped = await waitForEmitter(
    session.stateChange,
    () => String(session.state) === 'Terminated',
    prospectBridged ? timeouts.bridgedReapMs : timeouts.reapMs,
  );
  if (reaped) return { outcome: 'released', prospectBridged };
  if (stuckLineFallback({ prospectBridged }) === 'keep') return { outcome: 'kept', reason: 'line_not_released' };
  // The conversation can end without wrap-up (an inbound release, a hot
  // transfer) and leave the seat free: re-read, so the BYE only lands on a
  // seat that still keeps it.
  const now = mediaRebuildSeatGate(await deps.readSeat());
  if (!now.go || !now.prospectBridged) return { outcome: 'kept', reason: 'conversation_ended' };
  await withTimeout(session.bye(), timeouts.signalingMs, 'SIP hangup timed out').catch(() => undefined);
  return { outcome: 'released', prospectBridged: true };
}

// The pool's line-restore path, driven from the phone: un-REGISTER (the pool
// holds the seat and reaps the dead leg on sofia::unregister), then
// re-REGISTER (it originates a fresh leg, which auto-answers on a new peer
// connection). The old line is hung up from here only when a prospect is on
// it — a BYE on a parked line reads to the pool as the line dropping.
export async function runMediaRebuild(deps: MediaRebuildRunDeps): Promise<MediaRebuildRunResult> {
  const { registerer, timeouts } = deps;
  if (!deps.phoneReady()) return { outcome: 'deferred', reason: 'phone_not_ready' };
  const gate = mediaRebuildSeatGate(await deps.readSeat());
  if (!gate.go) return { outcome: 'deferred', reason: 'seat' };
  if (!deps.phoneReady()) return { outcome: 'deferred', reason: 'phone_not_ready' };

  deps.setInFlight(true);
  try {
    let result: MediaRebuildRunResult = { outcome: 'kept', reason: 'line_not_released' };
    try {
      await withTimeout(registerer.unregister(), timeouts.signalingMs, 'SIP un-registration timed out');
      // The 200 is the switch having dropped the binding, and so having fired
      // the sofia::unregister the pool acts on.
      const unregistered = await waitForEmitter(
        registerer.stateChange,
        () => String(registerer.state) === 'Unregistered',
        timeouts.signalingMs,
      );
      if (!unregistered) {
        result = { outcome: 'kept', reason: 'unregister_unconfirmed' };
      } else {
        result = await releaseStuckLine(deps, gate.prospectBridged);
      }
    } catch {
      result = { outcome: 'kept', reason: 'unregister_failed' };
    }
    // Whatever happened above, the phone must not stay unregistered — and the
    // REGISTER is what gives a held seat its line back.
    if (deps.canReRegister()) {
      await withTimeout(registerer.register(), timeouts.signalingMs, 'SIP registration timed out').catch(() => {
        // Still waiting on the un-REGISTER: its late 200 reaches the
        // registerer's listener, which recovers as for any unregistration.
        if (String(registerer.state) !== 'Unregistered') return;
        try {
          deps.onReRegisterFailed();
        } catch {
          // The flag below must clear regardless.
        }
      });
    }
    return result;
  } finally {
    deps.setInFlight(false);
  }
}
